import React, { useCallback, useEffect,useMemo, useState } from 'react';

import CSSModule from 'react-css-modules';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppWrapper, CustomerCouponCard, Spinner, SeoHead } from 'components';
import { actions as appliedActions } from 'coupon-common/src/modules/customerCoupons/applied';
import { actions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { RouteNames } from '../../../constants';
import styles from './Favourites.module.scss';

const MyFavourites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allFavourite = useSelector(state => state.customerCoupons.favourite);
  const appliedState = useSelector(state => state.customerCoupons.applied);

  const { fetching, fetched, items, result, error } = allFavourite;
  const { results = [], nextPage } = result || {};

  const [selectedCategory, setSelectedCategory] = useState(null);

  const filteredList = useMemo(() => {
    let favList = results
      .map(res => items[res])
      .filter(Boolean)
      .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
      .filter((item, index, self) => {
        const isFirstInstance = self.findIndex(i => i.id === item.id) === index;
        const isDateActive = item.expiry_type === 'amount_limit' || !item.expiry || new Date(item.expiry) > new Date();
        const isNotExpiredByState = item.state !== 'expired';
        return isFirstInstance && isDateActive && isNotExpiredByState;
      });

    if (selectedCategory) {
      favList = favList.filter(coupon => coupon.category?.id === selectedCategory);
    }
    return favList;
  }, [results, items, selectedCategory]);

  const getAllFavourites = useCallback(
    params => {
      dispatch(actions.getAllFavouriteList(params));
    },
    [dispatch],
  );

  const getApplyCouponList = useCallback(
    params => {
      dispatch(appliedActions.getApplyCouponList(params));
    },
    [dispatch],
  );

  useEffect(() => {
    getApplyCouponList();
    getAllFavourites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reachedUserLimit = couponId => {
    const targetStates = new Set(['reserved', 'accepted', 'waiting']);
    const coupons = appliedState?.data?.results;
    if (!coupons) return false;

    const matching = coupons.filter(item => targetStates.has(item.state) && item.coupon === couponId);
    if (matching.length === 0) return false;
    return matching.length >= matching[0]?.coupon_details?.user_limit;
  };

  const goToDetails = coupon => {
    if (coupon.state === 'expired') {
      // eslint-disable-next-line no-alert
      window.alert('Coupon Expired\n\nSorry, this coupon has expired. It may be available again in the future.');
    } else {
      navigate(`${RouteNames.COUPONS_DETAILS}/${coupon.id}`);
    }
  };

  const goToBusinessDetails = businessId => {
    navigate(`${RouteNames.BUSINESSES_DETAILS}/${businessId}`);
  };

  const handleLoadMore = () => {
    if (nextPage) {
      getAllFavourites({ page: nextPage });
    }
  };

  const toggleFavouriteHandler = id => {
    const action = actions.removeFavourite(id);
    dispatchAsAsync(action, dispatch).then(() => {
      dispatch(actions.getAllFavouriteList());
    });
  };

  const displayList = filteredList.filter(item => !reachedUserLimit(item.id));

  return (
    
      <div styleName="page">
                      <SeoHead title="Favourites" path={RouteNames.FAVOURITES} noIndex />
        {error && <p styleName="state-message">Something went wrong loading your favourites.</p>}

        {!error && (!filteredList || (fetching && !fetched)) && (
          <div styleName="state-message">
            <Spinner show size="md" />
          </div>
        )}

        {!error && filteredList && !(fetching && !fetched) && displayList.length === 0 && (
          <p styleName="state-message">No favourite coupons found.</p>
        )}

        {!error && displayList.length > 0 && (
          <>
            <div styleName="grid">
              {displayList.map((item, index) => (
                <CustomerCouponCard
                  key={item.id}
                  first={index === 0}
                  data={item}
                  onPress={() => goToDetails(item)}
                  isFavourite
                  goToBusinessDetails={() => goToBusinessDetails(item.business?.id)}
                  toggleFavourite={() => toggleFavouriteHandler(item.id)}
                />
              ))}
            </div>
            {nextPage && (
              <div styleName="load-more-row">
                <button type="button" styleName="load-more-btn" onClick={handleLoadMore} disabled={fetching}>
                  {fetching ? <Spinner show size="sm" /> : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    
  );
};

export default CSSModule(MyFavourites, styles, { allowMultiple: true });
